import { nextLobbyForm } from '../series/creationDefaults';
import { useEffect, useMemo, useState } from 'react';
import type { LobbyInfo, LobbyRequest, LobbySettingsPatch } from '../../api/types';
import {
  CmPickNames,
  DotaTvDelayNames,
  GameMode,
  GameModeNames,
  LobbyVisibilityKeys,
  LobbyVisibilityNames,
  PauseSettingNames,
  PenaltyLevelNames,
  SeriesTypeNames,
  ServerRegionNames,
  type LobbyVisibilityKey,
} from '../../domain/enums';
import { useConfig } from '../../store/config';
import { Button, Checkbox, Dialog, Field, Select, TextInput } from '../../ui';
import {
  buildCreateFollowUpSettings,
  buildLobbyRequest,
  buildSettingsPayload,
  hasAdvancedValues,
  initialFormValues,
  validateForm,
  type LobbyFormValues,
} from './lobbyForm';

export type LobbyFormSubmit =
  | { mode: 'create'; afterCreated?: () => void; payload: LobbyRequest; followUp: LobbySettingsPatch | null }
  | { mode: 'edit'; botId: string; payload: LobbySettingsPatch };

function valuesFromLobby(lobby: LobbyInfo, base: LobbyFormValues): LobbyFormValues {
  return {
    ...base,
    game_name: lobby.game_name ?? '',
    password: lobby.pass_key ?? '',
    server_region: lobby.server_region ?? base.server_region,
    game_mode: lobby.game_mode ?? base.game_mode,
    league_id: lobby.league_id ?? 0,
    cm_pick: lobby.cm_pick ?? 0,
    allow_spectating: lobby.allow_spectating ?? true,
    allchat: lobby.allchat ?? false,
    priority: (lobby.selection_priority_rules ?? 1) === 1 ? 'auto' : 'manual',
    visibility: LobbyVisibilityKeys[lobby.visibility ?? 0] ?? 'public',
    allow_cheats: lobby.allow_cheats ?? false,
    pause_setting: lobby.pause_setting ?? 0,
    dota_tv_delay: lobby.dota_tv_delay ?? 0,
    penalty_level_radiant: lobby.penalty_level_radiant ?? 0,
    penalty_level_dire: lobby.penalty_level_dire ?? 0,
    series_type: lobby.series_type ?? 0,
    radiant_series_wins: lobby.radiant_series_wins ?? 0,
    dire_series_wins: lobby.dire_series_wins ?? 0,
    inviteMode: 'none',
  };
}

function options(map: Record<number, string>) {
  return Object.entries(map).map(([value, name]) => (
    <option key={value} value={value}>
      {name}
    </option>
  ));
}

function SectionTitle({ children }: { children: string }) {
  return (
    <h3 className="mb-3 font-sans text-xs font-semibold tracking-wide text-gold-300 uppercase">
      {children}
    </h3>
  );
}

export default function LobbyFormDialog({
  open,
  mode,
  lobby,
  submitting,
  onClose,
  onSubmit,
}: {
  open: boolean;
  mode: 'create' | 'edit';
  lobby?: LobbyInfo | null;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (submission: LobbyFormSubmit) => void;
}) {
  const { config } = useConfig();
  const [values, setValues] = useState<LobbyFormValues>(() => initialFormValues(config));
  const [touched, setTouched] = useState(false);
  const [more, setMore] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = initialFormValues(config);
    const next = lobby ? valuesFromLobby(lobby, base) : base;
    if (mode === 'create' && lobby) { next.game_name += ' copy'; next.radiant_series_wins = 0; next.dire_series_wins = 0; }
    setValues(next);
    setTouched(false);
    setMore(hasAdvancedValues(next));
  }, [open, mode, lobby]);

  const errors = useMemo(() => validateForm(values), [values]);
  const errorFor = (field: keyof LobbyFormValues) =>
    touched ? errors.find((error) => error.field === field)?.message : undefined;

  const set = <K extends keyof LobbyFormValues>(key: K, value: LobbyFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  const togglePlayer = (steamId: string) =>
    setValues((current) => ({
      ...current,
      selectedPlayerIds: current.selectedPlayerIds.includes(steamId)
        ? current.selectedPlayerIds.filter((id) => id !== steamId)
        : [...current.selectedPlayerIds, steamId],
    }));

  const handleSubmit = (another = false) => {
    setTouched(true);
    if (errors.length > 0) {
      if (errors.some((error) => error.field === 'radiant_series_wins')) setMore(true);
      return;
    }

    if (mode === 'edit' && lobby) {
      onSubmit({ mode: 'edit', botId: lobby.bot_id, payload: buildSettingsPayload(values) });
      return;
    }
    onSubmit({
      mode: 'create',
      afterCreated: another ? () => { setValues(nextLobbyForm(values)); setTouched(false); } : undefined,
      payload: buildLobbyRequest(values, config),
      followUp: buildCreateFollowUpSettings(values),
    });
  };

  const isSeries = Number(values.series_type) > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={mode === 'edit' ? 'Lobby settings' : 'Create lobby'}
      description={
        mode === 'edit'
          ? 'Changes are pushed to the live lobby.'
          : 'Defaults come from Settings. For a bot-run best-of series use Create series instead.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {mode === 'create' && <Button disabled={submitting} onClick={() => handleSubmit(true)}>Create and add another</Button>}
          <Button variant="primary" loading={submitting} onClick={() => handleSubmit()}>
            {mode === 'edit' ? 'Save settings' : 'Create lobby'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Lobby name" error={errorFor('game_name')}>
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              value={values.game_name}
              onChange={(event) => set('game_name', event.target.value)}
              placeholder="FOL #1234 ADVANCED CM"
            />
          )}
        </Field>

        <Field label="Password" hint="Leave empty for an open lobby">
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              value={values.password}
              onChange={(event) => set('password', event.target.value)}
            />
          )}
        </Field>

        <Field label="Server region">
          {({ id }) => (
            <Select
              id={id}
              value={values.server_region}
              onChange={(event) => set('server_region', Number(event.target.value))}
            >
              {options(ServerRegionNames)}
            </Select>
          )}
        </Field>

        <Field label="Game mode">
          {({ id }) => (
            <Select
              id={id}
              value={values.game_mode}
              onChange={(event) => set('game_mode', Number(event.target.value))}
            >
              {options(GameModeNames)}
            </Select>
          )}
        </Field>

        <Field label="League ID" error={errorFor('league_id')}>
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="number"
              min={0}
              value={values.league_id}
              onChange={(event) => set('league_id', Number(event.target.value))}
            />
          )}
        </Field>

        <Field
          label="Selection priority"
          hint={values.priority === 'auto' ? "Dota's coin toss decides side and first pick" : 'First pick set below'}
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={values.priority}
              onChange={(event) => set('priority', event.target.value as 'auto' | 'manual')}
            >
              <option value="auto">Automatic</option>
              <option value="manual">Manual</option>
            </Select>
          )}
        </Field>

        {Number(values.game_mode) === GameMode.CaptainsMode && (
          <Field label="First pick">
            {({ id }) => (
              <Select
                id={id}
                value={values.cm_pick}
                onChange={(event) => set('cm_pick', Number(event.target.value))}
              >
                {options(CmPickNames)}
              </Select>
            )}
          </Field>
        )}

        <div className="flex flex-col justify-end gap-2 pb-1">
          <Checkbox
            label="Allow spectating"
            checked={values.allow_spectating}
            onChange={(event) => set('allow_spectating', event.target.checked)}
          />
          <Checkbox
            label="All chat"
            checked={values.allchat}
            onChange={(event) => set('allchat', event.target.checked)}
          />
        </div>
      </div>

      <div className="mt-6 border-t border-line pt-5">
        <button
          type="button"
          onClick={() => setMore((show) => !show)}
          aria-expanded={more}
          className="text-xs text-ink-dim underline-offset-4 hover:text-ink hover:underline"
        >
          {more ? 'Hide more settings' : 'More settings: penalties, DotaTV, pauses, visibility, series'}
        </button>

        {more && (
          <div className="mt-4 space-y-6">
            <section>
              <SectionTitle>Draft and broadcast</SectionTitle>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Radiant penalty" hint="Reserve time taken from the Radiant draft">
                  {({ id, describedBy }) => (
                    <Select
                      id={id}
                      aria-describedby={describedBy}
                      value={values.penalty_level_radiant}
                      onChange={(event) => set('penalty_level_radiant', Number(event.target.value))}
                    >
                      {options(PenaltyLevelNames)}
                    </Select>
                  )}
                </Field>
                <Field label="Dire penalty" hint="Reserve time taken from the Dire draft">
                  {({ id, describedBy }) => (
                    <Select
                      id={id}
                      aria-describedby={describedBy}
                      value={values.penalty_level_dire}
                      onChange={(event) => set('penalty_level_dire', Number(event.target.value))}
                    >
                      {options(PenaltyLevelNames)}
                    </Select>
                  )}
                </Field>
                <Field label="DotaTV delay">
                  {({ id }) => (
                    <Select
                      id={id}
                      value={values.dota_tv_delay}
                      onChange={(event) => set('dota_tv_delay', Number(event.target.value))}
                    >
                      {options(DotaTvDelayNames)}
                    </Select>
                  )}
                </Field>
                <Field label="Pauses">
                  {({ id }) => (
                    <Select
                      id={id}
                      value={values.pause_setting}
                      onChange={(event) => set('pause_setting', Number(event.target.value))}
                    >
                      {options(PauseSettingNames)}
                    </Select>
                  )}
                </Field>
              </div>
            </section>

            <section>
              <SectionTitle>Access</SectionTitle>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Visibility">
                  {({ id }) => (
                    <Select
                      id={id}
                      value={values.visibility}
                      onChange={(event) => set('visibility', event.target.value as LobbyVisibilityKey)}
                    >
                      {Object.entries(LobbyVisibilityKeys).map(([value, key]) => (
                        <option key={key} value={key}>
                          {LobbyVisibilityNames[Number(value)]}
                        </option>
                      ))}
                    </Select>
                  )}
                </Field>
                <div className="flex flex-col justify-end gap-2 pb-1">
                  <Checkbox
                    label="Allow cheats"
                    checked={values.allow_cheats}
                    onChange={(event) => set('allow_cheats', event.target.checked)}
                  />
                </div>
              </div>
            </section>

            <section>
              <SectionTitle>Series display</SectionTitle>
              <p className="mb-3 text-xs text-ink-faint">
                What the Dota lobby shows for a series you run by hand. Nothing here chains games; use
                Create series for that.
              </p>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="Series">
                  {({ id }) => (
                    <Select
                      id={id}
                      value={values.series_type}
                      onChange={(event) => set('series_type', Number(event.target.value))}
                    >
                      {options(SeriesTypeNames)}
                    </Select>
                  )}
                </Field>
                <Field label="Radiant wins" error={errorFor('radiant_series_wins')}>
                  {({ id, describedBy }) => (
                    <TextInput
                      id={id}
                      aria-describedby={describedBy}
                      type="number"
                      min={0}
                      max={5}
                      disabled={!isSeries}
                      value={values.radiant_series_wins}
                      onChange={(event) => set('radiant_series_wins', Number(event.target.value))}
                    />
                  )}
                </Field>
                <Field label="Dire wins">
                  {({ id }) => (
                    <TextInput
                      id={id}
                      type="number"
                      min={0}
                      max={5}
                      disabled={!isSeries}
                      value={values.dire_series_wins}
                      onChange={(event) => set('dire_series_wins', Number(event.target.value))}
                    />
                  )}
                </Field>
              </div>
            </section>
          </div>
        )}
      </div>

      {mode === 'create' && (
        <div className="mt-6 border-t border-line pt-5">
          <Field label="Invites">
            {({ id }) => (
              <Select
                id={id}
                value={values.inviteMode}
                onChange={(event) =>
                  set('inviteMode', event.target.value as LobbyFormValues['inviteMode'])
                }
              >
                <option value="none">No invites</option>
                <option value="flat">Pick players</option>
                <option value="by_side">Pick teams per side</option>
              </Select>
            )}
          </Field>

          {values.inviteMode === 'flat' && (
            <div className="mt-3">
              {config.players.length === 0 ? (
                <p className="text-xs text-ink-faint">
                  No players configured yet — add them on the Players page.
                </p>
              ) : (
                <div className="grid max-h-56 gap-1 overflow-y-auto rounded-md border border-line bg-surface-2/50 p-3 sm:grid-cols-2 scrollbar-slim">
                  {config.players.map((player) => (
                    <Checkbox
                      key={player.steamId}
                      label={player.name || player.steamId}
                      checked={values.selectedPlayerIds.includes(player.steamId)}
                      onChange={() => togglePlayer(player.steamId)}
                    />
                  ))}
                </div>
              )}
              {errorFor('selectedPlayerIds') && (
                <p className="mt-2 text-xs text-bad">{errorFor('selectedPlayerIds')}</p>
              )}
            </div>
          )}

          {values.inviteMode === 'by_side' && (
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <Field label="Radiant team" error={errorFor('radiantTeamId')}>
                {({ id }) => (
                  <Select
                    id={id}
                    value={values.radiantTeamId}
                    onChange={(event) => set('radiantTeamId', event.target.value)}
                  >
                    <option value="">None</option>
                    {config.teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name || 'Unnamed team'}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              <Field label="Dire team">
                {({ id }) => (
                  <Select
                    id={id}
                    value={values.direTeamId}
                    onChange={(event) => set('direTeamId', event.target.value)}
                  >
                    <option value="">None</option>
                    {config.teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name || 'Unnamed team'}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            </div>
          )}

          {values.inviteMode !== 'none' && (
            <div className="mt-3">
              <Checkbox
                label="Only let invited Steam IDs take a slot"
                checked={values.enforce_verification_steam_ids}
                onChange={(event) => set('enforce_verification_steam_ids', event.target.checked)}
              />
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
